import React from "react";
import { find } from "lodash";
import { BusinessCardBox, LabelCheckBox, QuickMoreLabelBox, DescribeLabelBox, PrivacyDataToggle, MemberSecureCard } from "@icoach/components/";
import { Stack, Button } from "@curves/ui-base";
import { isEmpty, isEqual } from "@util/utils";
import { CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";
import { MEMBERS_MEMBER_DETAIL_TABS } from "@icoach/constants";

/**
 * 會員
 */
export const memberSheet = {
	cellKey: "memberSheet",
	headerLabel: "會員",
	width: 20,
	maxWidth: 23,
	align: "left",
	formatCell: (_value, rowData, { routerPath, isShowAvatar = true, isShowAvatarImage = true, onKeepState }) => {
		const handleOnClick = () => {
			onKeepState && onKeepState(rowData);
		};
		return (
			// TODO: USE MemberInfoCard
			<BusinessCardBox sourceData={rowData} routerPath={routerPath} isShowAvatar={isShowAvatar} isShowAvatarImage={isShowAvatarImage} onClick={handleOnClick} />
		);
	},
};

export const getMemberSheet = params => {
	const { isRevealed } = params || {};
	return {
		cellKey: "memberSheet",
		headerLabel: "會員",
		width: 20,
		maxWidth: 23,
		align: "left",
		formatCell: (_, rowData, other) => {
			const { memberID, displayName, maskDisplayName, nickName, memberNo, memberImg, isTransferOut = false, membershipStatus } = rowData;
			const { isShowAvatar = true, onKeepState } = other;
			const handleOnClick = () => {
				onKeepState && onKeepState(rowData);
			};
			return (
				<MemberSecureCard
					targetID={memberID}
					name={displayName}
					maskName={maskDisplayName}
					nickname={nickName}
					memberNo={!isTransferOut ? memberNo : "移出"}
					avatar={memberImg}
					membershipStatus={membershipStatus}
					link={!isTransferOut ? CLUB_MEMBERS_ROUTER.MEMBER_DETAIL.GET_TABS_PATH(memberID, MEMBERS_MEMBER_DETAIL_TABS.WORKOUT) : null}
					onClick={handleOnClick}
					isShowAvatar={isShowAvatar}
					isRevealed={isRevealed}
				/>
			);
		},
	};
};

/**
 * 手機(單純無切換)
 */
export const cellPhoneSheet = {
	cellKey: "cellPhone",
	headerLabel: "手機",
	width: 13,
	align: "center",
	formatCell: (cellPhone, { maskCellPhone }) => {
		return <PrivacyDataToggle fullData={cellPhone} maskedData={maskCellPhone} />;
	},
};

/**
 * 手機(遮罩切換)
 * @param {{
 *      isRevealed: boolean,        // 是否顯示完整手機號碼（true: 顯示，false: 遮罩）
 * }} params
 * @returns {{
 *      cellKey: string,        // 單元格的唯一鍵值
 *      headerLabel: string,    // 表頭標籤
 *      width: number,          // 欄位寬度
 *      align: string,          // 對齊方式（left、center、right）
 *      formatCell: (function(*, {maskCellPhone: *}): *)    // 處理單元格內容的函式
 * }}
 */
export const getCellPhoneSheet = params => {
	const { isRevealed } = params || {};

	return {
		cellKey: "cellPhone",
		headerLabel: "手機",
		width: 13,
		align: "center",
		formatCell: (cellPhone, { maskCellPhone }) => {
			return <PrivacyDataToggle fullData={cellPhone} maskedData={maskCellPhone} isRevealed={isRevealed} />;
		},
	};
};

/**
 * 手機(遮罩切換/快速名單切換)
 * @param {{
 *      isRevealed: boolean,        // 是否顯示完整手機號碼（true: 顯示，false: 遮罩）
 *      isShowContactGroup: boolean // 是否顯示聯絡按鍵(true: 顯示，false: 不顯示)
 * }} params
 * @returns {{
 *      cellKey: string,        // 單元格的唯一鍵值
 *      headerLabel: string,    // 表頭標籤
 *      width: number,          // 欄位寬度
 *      align: string,          // 對齊方式（left、center、right）
 *      formatCell: (function(*, {maskCellPhone: *}): *)    // 處理單元格內容的函式
 * }}
 */
export const getMemberFilterCellPhoneSheet = params => {
	const { isRevealed, isShowContactGroup } = params || {};

	return {
		cellKey: "cellPhone",
		headerLabel: "手機",
		width: 16,
		align: "center",
		formatCell: (cellPhone, rowData, { contactData, dialogRef }) => {
			const { maskCellPhone, isTransferOut = false, memberID } = rowData;
			const { filterType = 0 } = contactData || {};

			if (isTransferOut) return "-";

			let status = 0;
			let missedCallCount = 0;

			if (isShowContactGroup) {
				const { itemStatistics = [] } = contactData || {};
				const item = find(itemStatistics, ["memberID", Number(memberID)]);

				if (item) {
					status = item.status;
					missedCallCount = item.missedCallCount;
				}
			}

			return (
				<React.Fragment>
					<PrivacyDataToggle fullData={cellPhone} maskedData={maskCellPhone} isRevealed={isRevealed} />
					{isShowContactGroup && (
						<Stack spacing={1} className="stack-margin-top">
							<Button
								variant={isEqual(status, 1) ? "contained" : "outlined"}
								color={!isEqual(status, 2) ? "success" : "blue-gray"}
								size="xsmall"
								onClick={() => dialogRef.current.openContactGroupDialog(rowData, filterType, "1")}
							>
								已打
							</Button>
							<Button
								variant={isEqual(status, 2) ? "contained" : "outlined"}
								color={!isEqual(status, 1) ? "error" : "blue-gray"}
								size="xsmall"
								onClick={() => dialogRef.current.openContactGroupDialog(rowData, filterType, "2")}
							>
								未接{missedCallCount > 0 && <b>{missedCallCount}</b>}
							</Button>
						</Stack>
					)}
				</React.Fragment>
			);
		},
	};
};

/**
 * 聯繫紀錄
 */
export const contactSheet = {
	cellKey: "contact",
	headerLabel: "聯繫紀錄",
	width: 30,
	maxWidth: 30,
	align: "left",
	formatCell: (value, { memberID, isTransferOut = false }, { setContactState }) => {
		if (isTransferOut) return "-";
		return value ? <DescribeLabelBox {...value} targetID={memberID} peopleType={"2"} setContactState={setContactState} /> : null;
	},
};

/**
 * 聯繫紀錄 (顧客/名單)
 */
export const getCustomerContactSheet = {
	cellKey: "contact",
	headerLabel: "聯繫紀錄",
	width: 30,
	maxWidth: 30,
	align: "left",
	formatCell: (cellValue, { customerID }) => {
		return !isEmpty(cellValue) ? <DescribeLabelBox {...cellValue} targetID={customerID} peopleType={"1"} /> : "";
	},
};

/**
 * Cell 勾選按鈕
 */
export const rowLabelCheckboxSheet = {
	cellKey: "memberID",
	isIcon: true,
	maxWidth: 7,
	width: 7,
	formatCell: (value, rowData, { checkBoxsRef, onCheckBoxChange, getCheckedItems, sequence }) => {
		let isChecked = false;
		const key = `${value}_${sequence}`;
		if ((getCheckedItems() || []).includes(key)) isChecked = true;
		return (
			<LabelCheckBox
				ref={el => (checkBoxsRef.current[key] = el)}
				onChange={onCheckBoxChange}
				idKey={key}
				defalutCehecked={isChecked}
				sourceData={rowData}
				disabled={isEqual(rowData.redeemStatus, 1)}
			/>
		);
	},
};

/**
 * 更多
 */
export const moreSheet = {
	cellKey: "more",
	headerLabel: "",
	width: 6.8,
	formatCell: (value, rowData, { dialogRef, routerPath, targetType, onKeepState }) => {
		const { isTransferOut } = rowData;
		if (isTransferOut) return null;
		const newRowData = { ...rowData, id: rowData.memberID, type: 2 };
		return <QuickMoreLabelBox rowData={newRowData} dialogRef={dialogRef} routerPath={routerPath} targetType={targetType} onKeepState={onKeepState} />;
	},
};
